import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { GENDERS } from 'components/intake/PatientInfoSection';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { usePortalSystems } from 'hooks/usePortalSystems';
import { CollectedFieldType, RetrievalFieldOption } from 'services/api/portalSystems';
import {
  finishManualPortal,
  launchManualPortal,
  ManualPortalField,
  onManualPortalResult,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';

import { retrievalFieldsFor } from '../constants';

// A retrieval field the typed form can render — everything but `documents`, which rides the
// capture affordance instead.
type TypedRetrievalField = RetrievalFieldOption & { type: Exclude<CollectedFieldType, 'documents'> };

export interface ManualLaunchRequest {
  workflowId: string;
  actionId: string;
  entryUrl: string;
  sourceSystem: string;
  // What the portal calls this patient, passed through so the HUD can help the person find them.
  portalPatientName?: string;
}

interface ManualLaunchState {
  // The task whose portal tab is open right now, or null. At most one, browser-wide: the
  // extension refuses a second manual job, so the UI arbitrates rather than letting a person
  // discover the refusal at click time.
  activeWorkflowId: string | null;
  launchingWorkflowId: string | null;
  // Tasks whose `Open in <portal>` has been acked this session. Session-scoped on purpose —
  // persisting it would need new run state, and a reload legitimately means "start over".
  unlockedWorkflowIds: Set<string>;
  launch: (request: ManualLaunchRequest) => Promise<void>;
  close: () => void;
}

const ManualLaunchContext = createContext<ManualLaunchState | null>(null);

/**
 * Owns the `Open in <portal>` launch for the whole page.
 *
 * It lives ABOVE the card list on purpose. `launchId` used to be component state on the one
 * card that offered the button; on a list that inserts and removes cards live, any refetch
 * that remounts a card would tear down the result subscription AND skip `manualClose`, leaving
 * the extension's single job slot occupied so every later launch fails.
 */
export function ManualLaunchProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToastActions();
  const { facilities: allowedFacilities } = useAllowedFacilities();
  const { systems: portalSystems } = usePortalSystems();
  const [active, setActive] = useState<{ launchId: string; workflowId: string } | null>(null);
  const [launchingWorkflowId, setLaunchingWorkflowId] = useState<string | null>(null);
  const [unlockedWorkflowIds, setUnlocked] = useState<Set<string>>(new Set());

  // The teardown path reads the live launch without re-running the unmount effect on every
  // launch — a dependency on `active` would fire manualClose the moment a launch resolved.
  const activeRef = useRef(active);
  activeRef.current = active;

  // Abandoning a task is the normal case on a list (unlike the single action card this moved
  // off, where completion was the only exit), so the tab is closed on teardown too.
  useEffect(
    () => () => {
      if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    },
    []
  );

  useEffect(() => {
    if (!active) return;
    return onManualPortalResult(active.launchId, () => {
      // The HUD's send (or a closed tab's drafts) ends the launch. The captured values are the
      // slideout's business — it owns the action write — and the card's job is done once the
      // task is unlocked, which the ack already did.
      setActive(null);
    });
  }, [active]);

  const launch = useCallback(
    async ({ workflowId, actionId, entryUrl, sourceSystem, portalPatientName }: ManualLaunchRequest) => {
      if (activeRef.current) {
        addToast({ text: 'Close the portal tab you already have open before opening another.' });
        return;
      }
      setLaunchingWorkflowId(workflowId);
      try {
        // The same declaration the retrieval form renders, described to the HUD: it has no idea
        // what any field means, so the datatype and the options travel with it. The field set
        // is this portal's configured selection (OLIO-3074); attachments ride the capture
        // affordance, not the typed form.
        const facilityOptions = allowedFacilities.map((group) => ({ value: group.id, label: group.name }));
        const fields: ManualPortalField[] = retrievalFieldsFor(portalSystems, sourceSystem)
          .filter((field): field is TypedRetrievalField => field.type !== 'documents')
          .map(({ name, label, type, cardinality }) => ({
            name,
            label,
            cardinality,
            type: type === 'facility' ? 'select' : type === 'ssn' ? 'text' : type,
            ...(type === 'facility' ? { options: facilityOptions } : {}),
            ...(type === 'select' ? { options: GENDERS } : {}),
          }));
        const { launchId } = await launchManualPortal({
          entryUrl,
          expectedOutputs: fields.map((field) => field.name),
          fields,
          params: { patient_name: portalPatientName ?? '' },
          context: { workflowId, actionId, sourceSystem, retrievalPointer: entryUrl },
        });
        setActive({ launchId, workflowId });
        setUnlocked((current) => new Set(current).add(workflowId));
      } catch (error) {
        addToast({ text: error instanceof Error ? error.message : 'Could not open the portal tab.' });
      } finally {
        setLaunchingWorkflowId(null);
      }
    },
    [addToast, allowedFacilities, portalSystems]
  );

  const close = useCallback(() => {
    if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    setActive(null);
  }, []);

  const value = useMemo(
    () => ({
      activeWorkflowId: active?.workflowId ?? null,
      launchingWorkflowId,
      unlockedWorkflowIds,
      launch,
      close,
    }),
    [active, launchingWorkflowId, unlockedWorkflowIds, launch, close]
  );

  return <ManualLaunchContext.Provider value={value}>{children}</ManualLaunchContext.Provider>;
}

export function useManualLaunch(): ManualLaunchState {
  const context = useContext(ManualLaunchContext);
  if (!context) throw new Error('useManualLaunch must be used inside a ManualLaunchProvider');
  return context;
}
